// ── Installer Proof Enforcement Policy ──
// Layer 3a — governs whether missing photos / checklist items BLOCK or
// merely WARN at phase submit time. Independent of execution strictness
// (which governs post-submit edits + future-phase visibility).
//
// Policies:
//   • strict_proof   — both photos AND checklist BLOCK (default)
//   • checklist_only — checklist BLOCKS, photos WARN+LOG
//   • photo_only     — photos BLOCKS, checklist WARNS+LOG
//   • advisory       — both WARN+LOG; submit allowed
//
// Hard rule (per spec): at least ONE proof type must be present in ALL
// modes. A submit with zero photos AND zero checks is never allowed,
// even under advisory.
//
// JobSpec definitions are NOT modified by this layer. The spec still
// declares what is "required" — this layer only governs how strictly
// missing items are enforced.

import type { JobSpecPhase, RequiredPhotoType } from "./jobSpec";
import type { PhaseExecution } from "./jobSpecExecution";

export type ProofPolicy =
  | "strict_proof"
  | "checklist_only"
  | "photo_only"
  | "advisory";

export const PROOF_POLICY_LABEL: Record<ProofPolicy, string> = {
  strict_proof: "Strict Proof",
  checklist_only: "Checklist Only",
  photo_only: "Photo Only",
  advisory: "Flexible (Advisory)",
};

export const PROOF_POLICY_DESCRIPTION: Record<ProofPolicy, string> = {
  strict_proof:
    "Both photos and checklist must be complete to submit. Most rigorous. Recommended default.",
  checklist_only:
    "Checklist must be complete; missing photos surface a warning and are logged for review.",
  photo_only:
    "Required photos must be captured; missing checklist items surface a warning and are logged.",
  advisory:
    "Submit allowed with gaps — every missing item is logged for audit. At minimum, one piece of proof is still required.",
};

export interface ProofPolicyAuditEntry {
  id: string;
  changedAt: string; // ISO
  changedBy: string;
  from: ProofPolicy;
  to: ProofPolicy;
  reason?: string;
}

export interface ProofGapEvent {
  id: string;
  capturedAt: string;
  jobId: string;
  jobLabel: string;
  phaseId: string;
  phaseLabel: string;
  installer: string;
  missingPhotos: string[];   // RequiredPhotoType ids that were absent
  missingChecklist: string[]; // checklist item ids that were unchecked
  policyAtSubmit: ProofPolicy;
  /**
   * Layer 3d — attribution hardening.
   * Captured at emission time from job.boundJobSpec so historical events
   * remain attributed to the spec version under which they were submitted,
   * even if the job is later rebound to a newer version.
   * Optional for backward compatibility with pre-3d events.
   */
  specId?: string;
  specVersion?: number;
}

export interface PhaseSubmitEvaluation {
  /** True if submit should be allowed under the active policy. */
  canSubmit: boolean;
  /** Reasons that BLOCK submit (red — must be resolved). */
  blockingReasons: string[];
  /** Reasons that allow submit but should be logged (amber). */
  warningReasons: string[];
  /** Required photo types missing from the phase exec. */
  missingPhotos: RequiredPhotoType[];
  /** Checklist item ids missing from the phase exec. */
  missingChecklist: string[];
  /** True if any proof exists at all (>=1 photo or >=1 check). */
  hasAnyProof: boolean;
}

/**
 * Evaluate whether a phase can be submitted under the given proof policy.
 * Pure function — no side effects, used by both UI and submit handler.
 */
export function evaluatePhaseSubmit(
  specPhase: JobSpecPhase,
  phase: PhaseExecution,
  policy: ProofPolicy,
): PhaseSubmitEvaluation {
  const missingPhotos = specPhase.requiredPhotos.filter(
    (pt) => !phase.photos.some((p) => p.photoType === pt),
  );
  const missingChecklist = specPhase.checklistItems
    .filter((ci) => !phase.checklist.find((c) => c.itemId === ci.id)?.checked)
    .map((ci) => ci.id);

  const hasAnyPhoto = phase.photos.length > 0;
  const hasAnyCheck = phase.checklist.some((c) => c.checked);
  const hasAnyProof = hasAnyPhoto || hasAnyCheck;

  const blockingReasons: string[] = [];
  const warningReasons: string[] = [];

  // Hard rule: at least one piece of proof in ALL modes.
  // Only applies when the spec actually defines requirements for this phase.
  const phaseHasRequirements =
    specPhase.requiredPhotos.length > 0 || specPhase.checklistItems.length > 0;
  if (phaseHasRequirements && !hasAnyProof) {
    blockingReasons.push(
      "At least one photo or checklist item is required to submit.",
    );
  }

  const photoMsg =
    missingPhotos.length === 1
      ? "1 required photo missing"
      : `${missingPhotos.length} required photos missing`;
  const checkMsg =
    missingChecklist.length === 1
      ? "1 checklist item incomplete"
      : `${missingChecklist.length} checklist items incomplete`;

  switch (policy) {
    case "strict_proof":
      if (missingPhotos.length > 0) blockingReasons.push(photoMsg);
      if (missingChecklist.length > 0) blockingReasons.push(checkMsg);
      break;
    case "checklist_only":
      if (missingChecklist.length > 0) blockingReasons.push(checkMsg);
      if (missingPhotos.length > 0) warningReasons.push(`${photoMsg} — logged`);
      break;
    case "photo_only":
      if (missingPhotos.length > 0) blockingReasons.push(photoMsg);
      if (missingChecklist.length > 0) warningReasons.push(`${checkMsg} — logged`);
      break;
    case "advisory":
      if (missingPhotos.length > 0) warningReasons.push(`${photoMsg} — logged`);
      if (missingChecklist.length > 0) warningReasons.push(`${checkMsg} — logged`);
      break;
  }

  return {
    canSubmit: blockingReasons.length === 0,
    blockingReasons,
    warningReasons,
    missingPhotos,
    missingChecklist,
    hasAnyProof,
  };
}
